<template>
  <b-container ref="scrollPlans" fluid :class="{ 'scroll-x': alternativeLayout }">
    <div v-if="cantDownloadReason" class="d-flex">
      <div class="mr-2">
        <div class="alert-circle d-flex align-items-center justify-content-center">
          <span>!</span>
        </div>
      </div>
      <div class="px-2 d-flex align-items-center justify-content-center">
        <p class="message">{{ $t(`main.modalReason.${cantDownloadReason}.text`) }}</p>
      </div>
    </div>
    <b-row id="recommended-plans" class="plans-container" :class="lifetimeLayout">
      <div ref="scrollPlansInner" class="plans-switch-container">
        <div
          v-show="showSwitchPlans"
          class="mb-4 plan-switch-container"
          :class="{ 'with-billed': showBilledInfo }"
        >
          <SwitchPlan
            class="switch-element"
            :annual-discount="annualDiscount"
            v-bind="switchProps"
            @switch-plans="switchPlan"
          />
          <p v-if="alternativeLayout && prismicProps.description" class="switch-text">
            {{ prismicProps.description }}
          </p>
        </div>

        <div
          class="plans-container-internal"
          :class="{ 'd-flex justify-content-around': alternativeLayout }"
        >
          <Plan
            v-for="(plan, i) in shownPlans"
            :key="i"
            v-model="annual"
            :plan-index="i"
            :plans-length="plansLength"
            :plan="plan"
            :is-free-user="isFreeUser"
            :see-more-link="seeMoreLink"
            :lifetime="lifetime"
            :is-design-plan="isDesignPlan"
            :prismic-props="prismicProps"
            :custom-plan="hasCustomPlans"
            :value-switch="isSwitchActive"
            :loading="isLoading"
            :u-id="uId"
            :alternative-layout="alternativeLayout"
            @subscribe-click="subscribeClick"
          />
        </div>
      </div>
    </b-row>
  </b-container>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import Plan from '@/components/PurchaseFlow/Plans/Plan';
import SwitchPlan from '@/components/PurchaseFlow/Plans/SwitchPlan';

export default {
  components: {
    Plan,
    SwitchPlan
  },
  props: {
    individualVectorPlans: {
      type: Boolean,
      default: false
    },
    seeMoreLink: {
      type: String,
      required: false,
      default: ''
    },
    lifetime: {
      type: Boolean,
      default: false,
      required: false
    },
    cantDownloadReason: {
      type: String,
      required: false,
      default: ''
    },
    prismicProps: {
      type: Object,
      default: () => ({})
    },
    customPlans: {
      type: Array,
      default: () => [],
      required: false
    },
    uId: {
      type: Number,
      required: true
    },
    annualDiscount: {
      type: Number,
      default: 25,
      required: false
    },
    alternativeLayout: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  data() {
    return {
      annual: true,
      isSwitchActive: true
    };
  },
  computed: {
    ...mapGetters('plans', ['plansWithIndividual', 'plans', 'lifetimePlans', 'getPlanToFetch']),
    ...mapState('vectors', ['actualVector']),
    isFreeUser() {
      return !this.$auth.loggedIn || this.$auth.user.idPlan === 1 ? true : false;
    },
    lifetimeLayout() {
      let className = this.alternativeLayout
        ? 'alternative'
        : this.lifetime
        ? 'justify-content-center plans-gap'
        : this.showRecommendedStyles
        ? 'justify-content-around'
        : 'space-evenly';
      return className;
    },
    shownPlans() {
      if (this.isLoading) {
        return this.getPlanToFetch(this.uId).amount;
      }
      if (this.hasCustomPlans) {
        return this.customPlans;
      }
      if (this.individualVectorPlans) {
        return this.plansWithIndividual;
      }
      if (this.lifetime) {
        return this.lifetimePlans;
      }
      return this.plans;
    },
    isDesignPlan() {
      return this.actualVector ? !!this.plansWithIndividual[0]?.extended : false;
    },
    isLoading() {
      return this.hasCustomPlans
        ? this.customPlans.length === 0
        : this.plansWithIndividual?.length === 0 && this.plans?.length === 0;
    },
    hasCustomPlans() {
      return this.primaryIdsLength > 0 || this.customPlans?.length > 0;
    },
    primaryIdsLength() {
      return this.prismicProps?.primaryIds?.length;
    },
    showRecommendedStyles() {
      return this.shownPlans?.length === 3;
    },
    plansLength() {
      return this.isLoading ? this.shownPlans : this.shownPlans?.length;
    },
    showSwitchPlans() {
      const prismicShowSwitch =
        Object.prototype.hasOwnProperty.call(this.prismicProps, 'showSwitch') &&
        this.prismicProps.showSwitch != null;

      return !this.lifetime && (prismicShowSwitch ? this.prismicProps.showSwitch : true);
    },
    switchProps() {
      return this.alternativeLayout
        ? {
            activePillColor: '#fff',
            activePillTextColor: '#2E2E2E'
          }
        : {};
    },
    showBilledInfo() {
      return (this.isSwitchActive && this.showSwitchPlans) || this.individualVectorPlans;
    }
  },
  watch: {
    primaryIdsLength() {
      this.$store.dispatch('plans/LOAD_PLANS');
    }
  },
  async mounted() {
    if (this.alternativeLayout) {
      const notScrolled =
        this.$refs.scrollPlansInner.clientWidth - this.$refs.scrollPlans.clientWidth;
      this.$refs.scrollPlans.scroll({ left: notScrolled / 2 + 25, behavior: 'smooth' });
    }
    if (!this.hasCustomPlans) {
      await this.$store.dispatch('plans/LOAD_PLANS');
    }
  },
  methods: {
    subscribeClick(plan) {
      this.$cookies.set('vx.plan', plan);
      this.$store.dispatch('plans/OPEN_PLAN', plan);
      this.$emit('suscribe-click', plan);
    },
    switchPlan(value) {
      this.isSwitchActive = value;
    }
  }
};
</script>
<style lang="scss">
.scroll-x {
  overflow-y: hidden;
  padding: 10px 0;

  width: 100vw;
  margin-left: calc(50% - 50vw);
  display: flex;
  justify-content: start;
  @include min-md {
    justify-content: center;
  }
}
.plan-switch-container {
  width: 100%;
}
.plans-switch-container {
  width: 100%;
  .plans-container-internal {
    display: flex;
    justify-content: space-evenly;
    flex-wrap: wrap;
  }
}
.alternative {
  &.plans-container {
    .plans-switch-container {
      display: flex;
      box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
      margin: 20px;
      border-radius: 15px;
      background: white;
      @include xl {
        margin-left: 35px;
      }
      @media (max-width: 1024px) {
        display: block;
        box-shadow: none;
        background: transparent;
      }
      .plans-container-internal {
        flex-wrap: nowrap;
        @media (max-width: 1024px) {
          margin-top: 60px;
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
          border-radius: 15px;
        }
      }
    }

    background: transparent;
    display: flex;
    flex-wrap: nowrap;
    width: min-content;
    .plan-switch-container {
      display: flex;
      flex-direction: column;
      position: relative;
      bottom: 0;
      height: 14.5rem;
      font-size: 13px;
      min-width: 265px;
      align-self: flex-end;
      padding: 0 10px 0 30px;
      @media (max-width: 1024px) {
        top: 0;
        position: absolute;
      }
      .switch-element {
        @media (max-width: 1024px) {
          margin-bottom: 30px;
          left: -15px;
          position: absolute;
          width: 100vw;
        }
      }
      .switch-text {
        font-size: 15px;
        margin-bottom: 30px;
        margin-top: 37px;
        padding-left: 10px;
        @media (max-width: 1024px) {
          display: none;
        }
      }
    }
  }
}
.alert-circle {
  color: #fff;
  width: 40px;
  height: 40px;
  border-radius: 200%;
  background-color: #ff4747;
}
.message {
  color: #4d4d4d;
  margin: 0;
  display: flex;
  align-items: center;
  text-align: center;
}
.plans-gap {
  gap: 26px;
}
.space-evenly {
  justify-content: space-evenly;
}
</style>
